import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { getDealPricing } from '../../utils/dealPricing';
import { categoryIntro, categoryTitle } from '../../utils/categorySlug';

const SITE_NAME = 'Lite Pocket Deals';
const BASE_TITLE = 'Best Amazon Deals & Discounts Today';
const BASE_DESCRIPTION = 'Discover the best Amazon deals, discounts, and promo codes. Save money on electronics, home goods, fashion, and more with our curated daily deals.';

/** Title/description/keywords for the grid, from the current search and visible deals. */
export const buildGridSeo = ({ searchTerm, deals, landing }) => {
  if (landing && !searchTerm) {
    return {
      title: categoryTitle(landing),
      description: categoryIntro(landing).text,
      keywords: `${landing.name} deals, ${landing.name} promo codes, amazon ${landing.name} discounts`,
    };
  }

  if (searchTerm) {
    return {
      title: `${searchTerm} Deals - Best Discounts & Offers | ${SITE_NAME}`,
      description: `Find the best ${searchTerm} deals and discounts on Amazon. Save money with verified promo codes and exclusive offers on ${searchTerm} products.`,
      keywords: `${searchTerm} deals, ${searchTerm} discounts, ${searchTerm} promo codes, ${searchTerm} offers, amazon ${searchTerm} deals`,
    };
  }

  if (deals.length > 0) {
    // Real categories of the first deals (the old version guessed them from title words)
    const categories = [...new Set(deals.slice(0, 10).map((d) => d.category).filter(Boolean))]
      .slice(0, 5)
      .map((c) => String(c).toLowerCase())
      .join(', ') || 'products';
    return {
      title: `${BASE_TITLE} - ${deals.length}+ Live Offers | ${SITE_NAME}`,
      description: `${BASE_DESCRIPTION} Currently featuring ${deals.length}+ live deals on ${categories} and more.`,
      keywords: `amazon deals, best deals, discounts, promo codes, ${categories}, daily deals, savings`,
    };
  }

  return {
    title: `${BASE_TITLE} | ${SITE_NAME}`,
    description: BASE_DESCRIPTION,
    keywords: 'amazon deals, best deals, discounts, promo codes, daily deals, savings, coupons, offers',
  };
};

const itemListJsonLd = (deals, origin) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Best Amazon Deals',
  description: 'Curated list of the best Amazon deals and discounts',
  numberOfItems: deals.length,
  itemListElement: deals.slice(0, 10).map((deal, index) => {
    const { salePrice } = getDealPricing(deal);
    return {
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Product',
        name: deal.title,
        description: deal.title,
        image: deal.image_url,
        url: `${origin}/deals/${deal._id}`,
        offers: {
          '@type': 'Offer',
          ...(salePrice !== null && { price: salePrice.toFixed(2) }),
          priceCurrency: 'USD',
          availability: 'https://schema.org/InStock',
          url: deal.url,
        },
      },
    };
  }),
});

/** <head> tags + schema.org data for the deal grid page. */
const DealGridSeo = ({ searchTerm, deals, landing }) => {
  const { origin } = window.location;
  // One canonical URL per page: search, sort and filters are views of the homepage
  // (or of the category page), not separate pages for Google to index.
  const canonical = landing ? `${origin}/category/${landing.slug}` : `${origin}/`;
  const seo = buildGridSeo({ searchTerm, deals, landing });
  const image = `${origin}/favicon.ico`;

  return (
    <Helmet>
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      <meta name="keywords" content={seo.keywords} />

      <meta property="og:type" content="website" />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:image" content={image} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={image} />

      <meta name="robots" content="index, follow" />
      <meta name="author" content={SITE_NAME} />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <link rel="canonical" href={canonical} />

      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: SITE_NAME,
          url: origin,
          description: seo.description,
          potentialAction: {
            '@type': 'SearchAction',
            target: `${origin}/?search={search_term_string}`,
            'query-input': 'required name=search_term_string',
          },
        })}
      </script>

      {deals.length > 0 && (
        <script type="application/ld+json">{JSON.stringify(itemListJsonLd(deals, origin))}</script>
      )}

      {landing && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: `${origin}/` },
              { '@type': 'ListItem', position: 2, name: `${landing.name} Deals`, item: canonical },
            ],
          })}
        </script>
      )}
    </Helmet>
  );
};

DealGridSeo.propTypes = {
  searchTerm: PropTypes.string,
  deals: PropTypes.arrayOf(PropTypes.object).isRequired,
  landing: PropTypes.shape({ name: PropTypes.string, slug: PropTypes.string, maxPercentOff: PropTypes.number }),
};

export default DealGridSeo;
